import type { TextOverlay } from "./overlays";

// ─── Filter State (matches web definition in filters-drawer.tsx) ────────────

export interface FilterState {
  filterId: string | null;
  intensity: number;
}

// ─── Green Screen State ────────────────────────────────────────────────────

export type GreenScreenBackground =
  | {
      type: "color";
      color: string;
    }
  | {
      type: "image";
      sourceUri: string;
      blobKey?: string;
      blobUrl?: string;
      fit: "cover";
      sourceWidth?: number;
      sourceHeight?: number;
    };

export interface GreenScreenSettings {
  enabled: boolean;
  mode: "chroma";
  keyColor: string;
  similarity: number;
  blend: number;
  spill?: number;
  foregroundFit?: "contain" | "cover";
  foregroundPositionX?: number;
  foregroundPositionY?: number;
  foregroundScale?: number;
  background: GreenScreenBackground;
}

export interface ClipFraming {
  fit: "contain" | "cover";
  positionX: number;
  positionY: number;
  scale?: number;
}

// ─── Timeline Clip ──────────────────────────────────────────────────────────

export interface TimelineClip {
  id: string;
  sourceUri: string;
  type: "video" | "image";
  sourceDuration: number;
  sourceWidth: number;
  sourceHeight: number;
  /** Absolute start time on the editor timeline. Omitted by older compact projects. */
  timelineStart?: number;
  trimStart: number;
  trimEnd: number;
  speed: number; // 0.25 - 4x
  volume: number; // 0-1
  displayDuration: number; // for images only (seconds)
  filter: FilterState | null;
  framing?: ClipFraming;
  greenScreen?: GreenScreenSettings | null;
  // Phase C — Vercel Blob upload metadata for non-primary clips. Present
  // when the clip was added via add-clip and uploaded to clips/{userId}/...;
  // absent for the primary clip whose source lives on Mux. The persisted
  // serializer reads these to emit `source: { kind: "blob", url, blobKey }`
  // for the worker; the worker fills the primary clip's `source` from
  // post.muxAssetId.
  blobKey?: string;
  blobUrl?: string;
}

// ─── Transitions ────────────────────────────────────────────────────────────

export type TransitionType =
  | "cut"
  | "crossfade"
  | "fade-to-black"
  | "fade-to-white"
  | "slide-left"
  | "slide-right"
  | "slide-up"
  | "slide-down"
  | "zoom-in"
  | "zoom-out"
  | "spin"
  | "glitch"
  | "wipe-left"
  | "wipe-right"
  | "blur"
  // Intro/outro-only types. `fade-in` and `fade-in-white` are valid
  // values for `introTransition`; `fade-out` and `fade-out-white` are
  // valid for `outroTransition`. They share the TransitionType union
  // (rather than getting their own enum) so the renderer and picker
  // can dispatch on a single field, but the TransitionPicker filters
  // to a context-appropriate subset.
  | "fade-in"
  | "fade-in-white"
  | "fade-out"
  | "fade-out-white";

export interface TimelineTransition {
  id: string;
  type: TransitionType;
  duration: number; // 0.3 - 2.0s
}

// ─── Text Overlays with Timing ──────────────────────────────────────────────

export type EntryAnimation =
  | "none"
  | "fade"
  | "slide-up"
  | "scale"
  | "typewriter";
export type ExitAnimation = "none" | "fade" | "slide-down" | "scale";

export interface TimelineTextOverlay extends TextOverlay {
  startTime: number;
  endTime: number;
  entryAnimation: EntryAnimation;
  exitAnimation: ExitAnimation;
}

// ─── Audio Tracks ───────────────────────────────────────────────────────────

export interface VolumeKeyframe {
  time: number; // relative to track's timelineStart
  value: number; // 0-1
}

export interface TimelineAudioTrack {
  id: string;
  type: "music" | "voiceover" | "sfx";
  sourceUri: string;
  sourceDuration: number;
  timelineStart: number;
  trimStart: number;
  trimEnd: number;
  volumeKeyframes: VolumeKeyframe[];
  musicTrackId?: number;
  title?: string;
  artist?: string;
  // Vercel Blob upload metadata for device-uploaded audio. Present when the
  // user added the track via file picker; absent for catalog tracks (which
  // serialize via musicTrackId instead). When set, the serializer emits a
  // { kind: "blob", url, blobKey } source so the worker can download it.
  blobKey?: string;
  blobUrl?: string;
}

// ─── Project ────────────────────────────────────────────────────────────────

export type AspectRatio = "9:16" | "16:9" | "1:1" | "4:5";

export interface TimelineProject {
  id: string;
  version: number;
  aspectRatio: AspectRatio;
  clips: TimelineClip[];
  transitions: TimelineTransition[]; // transitions[i] = between clips[i] and clips[i+1]
  // Optional fade-in at the very start of the composition. Only
  // intro-flavored TransitionType values (`fade-in`, `fade-in-white`,
  // or `cut` to disable) are meaningful here; the TransitionPicker
  // filters to those when context === "intro".
  introTransition?: TimelineTransition | null;
  // Optional fade-out at the very end of the composition. Mirrors
  // introTransition; valid types are `fade-out`, `fade-out-white`,
  // or `cut`.
  outroTransition?: TimelineTransition | null;
  textOverlays: TimelineTextOverlay[];
  audioTracks: TimelineAudioTrack[];
  outputWidth: number;
  outputHeight: number;
  // Local-only label used in the editor header and Drafts list. Never
  // surfaces to the feed/profile — stripped from the wire shape in
  // toPersistedTimelineProject.
  name?: string;
}

// ─── Capability descriptor ──────────────────────────────────────────────
// Returned from /api/timeline-capabilities. Lives in shared-types so both
// the web and Expo serializers can take the same TimelineCapabilities shape
// without web-only or expo-only imports leaking into shared code paths.

export interface TimelineCapabilities {
  supportsSplit: boolean;
  supportsSpeed: boolean;
  supportsVolume: boolean;
  supportsTransitions: boolean;
  supportsMultiClip: boolean;
  supportsGreenScreen: boolean;
  supportsClipFraming: boolean;
  maxClips: number;
  maxDurationSeconds: number;
}

export const DEFAULT_TIMELINE_CAPABILITIES: TimelineCapabilities = {
  supportsSplit: false,
  supportsSpeed: false,
  supportsVolume: false,
  supportsTransitions: false,
  supportsMultiClip: false,
  supportsGreenScreen: false,
  supportsClipFraming: false,
  maxClips: 1,
  maxDurationSeconds: 180,
};

// ─── Undo/Redo History ──────────────────────────────────────────────────────

export interface TimelineHistory {
  past: TimelineProject[];
  present: TimelineProject;
  future: TimelineProject[];
}

// ─── Actions ────────────────────────────────────────────────────────────────

export type TimelineAction =
  | { type: "SPLIT_CLIP"; clipId: string; atTime: number }
  | { type: "DELETE_CLIP"; clipId: string }
  | { type: "REORDER_CLIPS"; fromIndex: number; toIndex: number }
  | { type: "MOVE_CLIP"; clipId: string; timelineStart: number }
  | { type: "TRIM_CLIP"; clipId: string; trimStart: number; trimEnd: number; timelineStart?: number }
  // Image clips don't have a source/trim concept — their on-timeline length
  // is set by `displayDuration` directly. The trim handles in the editor
  // dispatch this action for image clips instead of TRIM_CLIP.
  | { type: "RESIZE_IMAGE_CLIP"; clipId: string; timelineStart: number; displayDuration: number }
  | { type: "SET_CLIP_SPEED"; clipId: string; speed: number }
  | { type: "SET_CLIP_VOLUME"; clipId: string; volume: number }
  | { type: "SET_CLIP_FILTER"; clipId: string; filter: FilterState | null }
  | { type: "SET_CLIP_FRAMING"; clipId: string; framing: ClipFraming | null }
  | { type: "SET_CLIP_GREEN_SCREEN"; clipId: string; greenScreen: GreenScreenSettings | null }
  | {
      type: "UPDATE_CLIP_GREEN_SCREEN";
      clipId: string;
      patch: Partial<GreenScreenSettings>;
    }
  | { type: "ADD_CLIP"; clip: TimelineClip; atIndex: number }
  | { type: "SET_TRANSITION"; index: number; transition: TimelineTransition }
  // Set or clear the optional bookend transitions on a project. Pass
  // null (or a `cut`-typed transition) to clear; otherwise the type
  // should be one of the intro-/outro-appropriate `fade-in*`/
  // `fade-out*` values.
  | { type: "SET_INTRO_TRANSITION"; transition: TimelineTransition | null }
  | { type: "SET_OUTRO_TRANSITION"; transition: TimelineTransition | null }
  | { type: "ADD_TEXT_OVERLAY"; overlay: TimelineTextOverlay }
  | {
      type: "UPDATE_TEXT_OVERLAY";
      id: string;
      patch: Partial<TimelineTextOverlay>;
    }
  | { type: "DELETE_TEXT_OVERLAY"; id: string }
  | { type: "ADD_AUDIO_TRACK"; track: TimelineAudioTrack }
  | {
      type: "UPDATE_AUDIO_TRACK";
      id: string;
      patch: Partial<TimelineAudioTrack>;
    }
  | { type: "DELETE_AUDIO_TRACK"; id: string }
  | { type: "SET_ASPECT_RATIO"; aspectRatio: AspectRatio }
  | { type: "SET_PROJECT_NAME"; name: string }
  | { type: "UNDO" }
  | { type: "REDO" }
  | { type: "LOAD_PROJECT"; project: TimelineProject }
  // Preview actions: apply changes without creating undo history entries.
  // Use during drag operations, then dispatch the normal action on mouseup.
  | { type: "TRIM_CLIP_PREVIEW"; clipId: string; trimStart: number; trimEnd: number; timelineStart?: number }
  | { type: "UPDATE_TEXT_OVERLAY_PREVIEW"; id: string; patch: Partial<TimelineTextOverlay> }
  | { type: "UPDATE_AUDIO_TRACK_PREVIEW"; id: string; patch: Partial<TimelineAudioTrack> };

// ─── Constants ──────────────────────────────────────────────────────────────

export const MAX_UNDO_HISTORY = 50;
export const FREE_DURATION_LIMIT = 180; // 3 minutes
export const PRO_DURATION_LIMIT = 600; // 10 minutes
export const MIN_CLIP_DURATION = 0.1; // seconds
export const DEFAULT_IMAGE_DURATION = 5; // seconds
export const MIN_SPEED = 0.25;
export const MAX_SPEED = 4;
export const SPEED_PRESETS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4];
export const MIN_TRANSITION_DURATION = 0.3;
export const MAX_TRANSITION_DURATION = 2.0;

export const ASPECT_RATIO_DIMENSIONS: Record<
  AspectRatio,
  { width: number; height: number }
> = {
  "9:16": { width: 1080, height: 1920 },
  "16:9": { width: 1920, height: 1080 },
  "1:1": { width: 1080, height: 1080 },
  "4:5": { width: 1080, height: 1350 },
};
